import {
  Box,
  Button,
  Code,
  Divider,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import React from 'react';
import { ApiError } from 'src/error/ApiError';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { UnprocessedValuation } from './types';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { useAddTestValuationUpdate } from 'src/secret-menu/bulk-valuations/hooks/useAddTestValuationUpdate';
import { useUpdateFinioFirmName } from 'src/secret-menu/bulk-valuations/hooks/useUpdateFinioFirmName';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ProviderSource } from 'src/company/providers/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';

const VALUATIONS_PROVIDER_DISPLAY_MAP: Record<string, string> = {
  ORIGO: 'Origo',
  FINIO: 'FINIO',
};

const normalizeValuationsProvider = (provider: string | null): string => {
  if (!provider) return 'Manually added';
  return VALUATIONS_PROVIDER_DISPLAY_MAP[provider] || provider;
};

export default function UnprocessedValuationUpdatesTab() {
  return <CompanyWrapper contentComponent={ValuationsContent} />;
}

function ExistingProviderSourcesTable({ tenantName }: { tenantName: string }) {
  const columns: DataTableColumn<ProviderSource>[] = [
    {
      accessor: 'provider',
      title: 'Provider',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.provider}
        </Text>
      ),
    },
    {
      accessor: 'valuationsProvider',
      title: 'Data source',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {normalizeValuationsProvider(ps.valuationsProvider)}
        </Text>
      ),
    },
    {
      accessor: 'lastUpdateDate',
      title: 'Last update',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.lastUpdateDate ? formatDate(ps.lastUpdateDate) : 'N/A'}
        </Text>
      ),
    },
    {
      accessor: 'count',
      title: 'Count',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.count}
        </Text>
      ),
    },
  ];

  return (
    <SimpleTable<ProviderSource>
      title="Active providers"
      mode="client"
      resource={`admin/companies/${tenantName}/provider-sources/existing`}
      columns={columns}
      showCount={true}
      enableSearch={false}
      pageSize={20}
      useLocalPagination
    />
  );
}

function UnmatchedProviderSourcesTable({ tenantName }: { tenantName: string }) {
  const columns: DataTableColumn<ProviderSource>[] = [
    {
      accessor: 'provider',
      title: 'Provider',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.provider}
        </Text>
      ),
    },
    {
      accessor: 'valuationsProvider',
      title: 'Data source',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {normalizeValuationsProvider(ps.valuationsProvider)}
        </Text>
      ),
    },
    {
      accessor: 'lastUpdateDate',
      title: 'Last update',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.lastUpdateDate ? formatDate(ps.lastUpdateDate) : 'N/A'}
        </Text>
      ),
    },
    {
      accessor: 'count',
      title: 'Count',
      sortable: true,
      render: (ps: ProviderSource) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {ps.count}
        </Text>
      ),
    },
  ];

  return (
    <SimpleTable<ProviderSource>
      title="Unmatched providers"
      mode="client"
      resource={`admin/companies/${tenantName}/provider-sources/unprocessed`}
      columns={columns}
      showCount={true}
      enableSearch={false}
      pageSize={20}
      useLocalPagination
    />
  );
}

function UnprocessedValuationsTable({ company }: { company: Company }) {
  const [selectedValuation, setSelectedValuation] = React.useState<UnprocessedValuation | null>(
    null,
  );

  const resource = `admin/companies/${company.id}/unprocessed-valuation-updates`;

  const columns = [
    {
      accessor: 'id',
      title: 'ID',
    },
    {
      accessor: 'providerReferenceNumber',
      title: 'Provider reference number',
    },
  ];

  return (
    <>
      <Modal
        opened={!!selectedValuation}
        onClose={() => setSelectedValuation(null)}
        title="Valuation content"
        size="xxl"
      >
        <Code block>{JSON.stringify(selectedValuation?.updateContent, null, 2)}</Code>
      </Modal>

      <SimpleTable<UnprocessedValuation>
        mode="server"
        resource={resource}
        title="Valuations"
        columns={columns}
        onRowClick={setSelectedValuation}
        showCount={false}
      />
    </>
  );
}

function FinioFirmNameSection({ company }: { company: Company }) {
  const { updateFinioFirmName, isLoading } = useUpdateFinioFirmName(company.id);
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] =
    useDisclosure(false);

  const form = useForm({
    initialValues: { finioFirmName: company.finioFirmName ?? '' },
  });

  const performUpdate = async (values: typeof form.values) => {
    try {
      await updateFinioFirmName({ finioFirmName: values.finioFirmName || null });
      form.resetDirty(values);
      window.location.reload();
    } catch (error) {
      const errorMessage =
        error instanceof ApiError ? error.message : 'An unexpected error occurred.';
      notifyAndLogError('Failed to update FINIO firm name', errorMessage, error);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (company.finioFirmName && values.finioFirmName !== company.finioFirmName) {
      openConfirmModal();
    } else {
      await performUpdate(values);
    }
  };

  const handleConfirm = async () => {
    closeConfirmModal();
    await performUpdate(form.values);
  };

  return (
    <Box>
      <Modal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
        title="Confirm FINIO Firm Name Change"
      >
        <Stack gap="md">
          <Text size="sm">
            This company already has a FINIO firm name configured. Changing it may affect bulk
            valuations matching.
          </Text>
          <Text size="sm">
            Current: <strong>{company.finioFirmName}</strong>
          </Text>
          <Text size="sm">
            New: <strong>{form.values.finioFirmName || '(empty)'}</strong>
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirmModal}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirm} loading={isLoading}>
              Confirm Change
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Text fw={FONT_WEIGHTS.semibold} size="lg" mb="sm">
        FINIO Configuration
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        Set the firm name that FINIO uses to identify this company in bulk valuations data.
      </Text>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm" maw={400}>
          <TextInput
            label="FINIO Firm Name"
            placeholder="Enter the firm name used by FINIO"
            {...form.getInputProps('finioFirmName')}
          />
          <Button type="submit" loading={isLoading} disabled={!form.isDirty()}>
            Save
          </Button>
        </Stack>
      </form>
    </Box>
  );
}

function ValuationsContent({ company }: { company: Company }) {
  const { addTestValuationUpdate, isLoading } = useAddTestValuationUpdate();

  const handleAddTestValuation = async () => {
    try {
      await addTestValuationUpdate();
      window.location.reload();
    } catch (error) {
      notifyAndLogError(
        'Error adding test valuation',
        'There was an error adding the test valuation. Please try again.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <FinioFirmNameSection company={company} />
      <Divider my="md" />
      <Flex gap="md">
        <div style={{ flex: 1 }}>
          <ExistingProviderSourcesTable tenantName={company.tenantName} />
        </div>
        <div style={{ flex: 1 }}>
          <UnmatchedProviderSourcesTable tenantName={company.tenantName} />
        </div>
      </Flex>

      <UnprocessedValuationsTable company={company} />

      <Button loading={isLoading} onClick={handleAddTestValuation}>
        Add test valuation
      </Button>
    </Stack>
  );
}
